import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import React from 'react';
import { SectionHeading } from './styled/SectionHeading';

// Type definitions
type ModalSize = 'xs' | 'sm' | 'md' | 'lg' | 'vertical';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string | React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: ModalSize;
  maxHeight?: string | number;
  PaperProps?: any;
  headerSx?: any;
  contentSx?: any;
  actionsSx?: any;
  contentDividers?: boolean;
  actionsDivider?: boolean;
  isTypeInModal?: boolean;
  isPriceListModal?: boolean;
}

const MODAL_HEIGHTS: Record<ModalSize, string | number> = {
  xs: 'auto',
  sm: 'auto',
  md: 'auto',
  lg: 'calc(100% - 20%)',
  vertical: 'calc(100% - 10%)',
};

const Modal: React.FC<ModalProps> = React.memo(function Modal({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  size = 'md',
  maxHeight = '80vh',
  PaperProps,
  headerSx,
  contentSx,
  actionsSx,
  contentDividers = false,
  actionsDivider = false,
  isTypeInModal = false,
  isPriceListModal = false,
}) {
  const height = MODAL_HEIGHTS[size] || MODAL_HEIGHTS.md;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      disableEnforceFocus
      disableRestoreFocus
      PaperProps={{
        sx: (theme) => {
          const width = theme.modalWidths[size] || theme.modalWidths.md;
          return {
            width: isPriceListModal ? 'calc(100% - 40%)' : width,
            height: isTypeInModal ? 'auto' : height,
            maxWidth: size === 'lg' ? '1430px' : '95vw',
            maxHeight: '90vh',
            m: 0,
            borderRadius: `${theme.borderRadius.md}px`,
            boxShadow: theme.customShadows.modal,
            border: `1px solid ${theme.palette.divider}`,
            overflow: 'hidden',
            ...(PaperProps?.sx || {}),
          };
        },
        ...PaperProps,
      }}
    >
      {(title || onClose !== undefined) && (
        <DialogTitle
          sx={(theme) => ({
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${theme.palette.divider}`,
            py: 2,
            pr: 1,
            pl: 3.5,
            ...(headerSx || {}),
          })}
        >
          <SectionHeading>{title}</SectionHeading>
          {subtitle && (
            <Typography
              sx={(theme) => ({
                fontWeight: 500,
                fontSize: 14,
                color: theme.palette.modal.subtitle || theme.palette.text.secondary,
                mt: 0.5,
                lineHeight: 1.4,
                letterSpacing: '0.01em',
                opacity: 0.8,
              })}
            >
              {subtitle}
            </Typography>
          )}
        </DialogTitle>
      )}
      <DialogContent
        dividers={contentDividers}
        sx={{
          p: 3,
          maxHeight,
          overflow: size === 'vertical' || size === 'lg' || size === 'sm' ? 'auto' : 'hidden',
          ...(contentSx || {}),
        }}
      >
        {children}
      </DialogContent>
      {footer && (
        <DialogActions
          sx={(theme) => ({
            p: 2,
            gap: 1,
            mb: 1,
            justifyContent: 'flex-end',
            ...(actionsDivider ? { borderTop: `1px solid ${theme.palette.divider}` } : {}),
            ...(actionsSx || {}),
          })}
        >
          {footer}
        </DialogActions>
      )}
    </Dialog>
  );
});
export default Modal;
